<template>
	<div
		class="rounded-lg sm:rounded-xl px-4 lg:px-4 pt-2 pb-4 lg:pb-4 shadow bg-white ring-orange-500"
	>
		<div class="grid grid-cols-2 gap-x-4">
			<div class="space-y-3 col-span-2 sm:col-span-1">
				<div v-if="field == 'revenue'" class="contents">
					<h2 class="text-xl sm:text-2xl text-gray-500 font-semibold mb-2">
						{{ __("messages.total_turnover") }}
					</h2>
					<p
						class="text-3xl sm:text-4xl text-gray-700 font-semibold whitespace-nowrap"
					>
						{{
							(1 * totals.revenue).toLocaleString(locale, {
								minimumFractionDigits: 0,
								maximumFractionDigits: 0,
							})
						}}
						€
					</p>
				</div>
				<div v-else class="contents">
					<h2 class="text-xl sm:text-2xl text-gray-500 font-semibold mb-2">
						{{ __("messages.total_quantity") }}
					</h2>
					<p class="text-3xl sm:text-4xl text-gray-700 font-semibold">
						{{
							(1 * totals.qty).toLocaleString(locale, {
								minimumFractionDigits: 0,
								maximumFractionDigits: 0,
							})
						}}
						t
					</p>
				</div>
			</div>
			<div
				class="flex-grow hidden sm:block"
				ref="chart"
				style="width: 100%; height: 200px"
			></div>
		</div>
	</div>
</template>

<script>
import { groupBy, sum, min, max, mean, range } from "lodash";
import * as echarts from "echarts";

export default {
	data: () => ({
		width: document.documentElement.clientWidth,
		height: document.documentElement.clientHeight,
	}),
	props: {
		contracts: {
			type: Array,
			required: true,
		},
		field: {
			type: String,
			default: "qty",
		},
	},
	data() {
		return {
			myChart: null,
		};
	},
	methods: {
		getSummaryStatQtySell(contracts) {
			return contracts.reduce(function (accum, cur) {
				if (cur.user_id == cur.seller_id) {
					return accum + (cur.qty__mt ?? 0);
				} else {
					return accum;
				}
			}, 0);
		},
		getSummaryStatQtyBuy(contracts) {
			return contracts.reduce(function (accum, cur) {
				if (cur.user_id == cur.buyer_id) {
					return accum + (cur.qty__mt ?? 0);
				} else {
					return accum;
				}
			}, 0);
		},
		getSummaryStatQty(contracts) {
			return (
				this.getSummaryStatQtySell(contracts) +
				this.getSummaryStatQtyBuy(contracts)
			);
		},
		getSummaryStatRevenueSell(contracts) {
			return contracts.reduce(function (accum, cur) {
				if (cur.user_id == cur.seller_id) {
					return (
						accum + Math.round((cur.qty__mt ?? 0) * (cur.price__mt ?? 0), 0)
					);
				} else {
					return accum;
				}
			}, 0);
		},
		getSummaryStatRevenueBuy(contracts) {
			return contracts.reduce(function (accum, cur) {
				if (cur.user_id == cur.buyer_id) {
					return (
						accum + Math.round((cur.qty__mt ?? 0) * (cur.price__mt ?? 0), 0)
					);
				} else {
					return accum;
				}
			}, 0);
		},
		getSummaryStatRevenue(contracts) {
			return (
				this.getSummaryStatRevenueSell(contracts) +
				this.getSummaryStatRevenueBuy(contracts)
			);
		},
	},
	mounted() {
		let chartDom = this.$refs.chart,
			that = this,
			option = {
				xAxis: {
					type: "category",
					data: this.dataByYear.map((z) => z.year),
					// splitLine: {
					//     lineStyle: {
					//         color: '#EBEBEA'
					//     }
					// },
					minorTick: {
						show: true,
					},
					minorSplitLine: {
						show: true,
					},
					axisLine: {
						show: true,
						lineStyle: {
							color: "#DFDFDE",
						},
					},
					axisLabel: {
						textStyle: {
							color: "#757575",
						},
					},
				},
				yAxis: [
					{
						type: "value",
						max:
							1.2 *
							Math.max.apply(
								Math,
								this.dataByYear.map((z) => z[this.field])
							),
						//max: 6100,
						splitLine: {
							show: true,
							lineStyle: {
								color: "#EBEBEA",
							},
						},
						minorTick: {
							show: true,
						},
						minorSplitLine: {
							show: true,
						},
						axisLine: {
							show: true,
							lineStyle: {
								color: "#EBEBEA",
							},
						},
					},
					{
						// second yAxis to have a right border
						// type: 'value',
						position: "right",
						axisLine: {
							show: true,
							lineStyle: {
								color: "#EBEBEA",
							},
						},
					},
				],
				tooltip: {
					trigger: "axis",
					axisPointer: {
						type: "shadow",
					},
					formatter: (params) => {
						let sign = that.field == "revenue" ? " €" : " t";
						return (
							`${params[0].seriesName}: ${params[0].value.toLocaleString(
								that.locale
							)} ` +
							sign +
							` <br />
                                ${
																	params[1].seriesName
																}: ${params[1].value.toLocaleString(
								that.locale
							)}` +
							sign
						);
					},
				},
				series: [
					{
						data: this.dataByYear.map(function (z) {
							return {
								value: z[that.field + "Sell"],
								itemStyle: {
									color: "#96BD0D",
								},
							};
						}),
						type: "bar",
						name: this.__("Sale"),
						barWidth: "30%",
						silent: true,
						stack: "x",
					},
					{
						data: this.dataByYear.map(function (z) {
							return {
								value: z[that.field + "Buy"],
								itemStyle: {
									color: "#f29300",
								},
							};
						}),
						type: "bar",
						name: this.__("Purchase"),
						label: {
							show: true,
							position: "top",
							color: "#454545",
							formatter: function (d) {
								console.log("DATA", that.field, d);
								return that.field == "revenue"
									? that.dataByYear[d.dataIndex][that.field].toLocaleString(
											that.locale
									  ) + " €"
									: that.dataByYear[d.dataIndex][that.field].toLocaleString(
											that.locale
									  ) + " t";
							},
						},
						barWidth: "30%",
						silent: true,
						stack: "x",
					},
				],
				textStyle: {
					fontFamily: "Barlow, Roboto, Helvetica, Arial, sans-serif",
					fontSize: "10",
					fontWeight: "500",
				},
				grid: {
					left: "auto",
					right: "1px",
					top: "14px",
					bottom: "21px",
				},
			};

		console.log("THERE", option.series);

		this.myChart = echarts.init(chartDom);
		this.myChart.setOption(option);
		that = this;
		window.addEventListener("resize", function () {
			that.myChart.resize();
		});

		//window.addEventListener('resize', this.myChart.resize());
	},
	computed: {
		totals() {
			return {
				qty: this.getSummaryStatQty(this.contracts ?? []),
				revenue: this.getSummaryStatRevenue(this.contracts ?? []),
			};
		},
		dataByYear() {
			let that = this,
				contractsByYear = groupBy(this.contracts, (c) => c.product__harvest),
				contracts = [],
				yearsWithContracts = Object.keys(contractsByYear),
				years = yearsWithContracts.map(function (y) {
					return parseInt(y);
				});
			years.map(function (year) {
				contracts.push({
					year: 1 * year,
					qtySell: that.getSummaryStatQtySell(contractsByYear[year] ?? []),
					qtyBuy: that.getSummaryStatQtyBuy(contractsByYear[year] ?? []),
					qty: that.getSummaryStatQty(contractsByYear[year] ?? []),
					revenueSell: that.getSummaryStatRevenueSell(
						contractsByYear[year] ?? []
					),
					revenueBuy: that.getSummaryStatRevenueBuy(
						contractsByYear[year] ?? []
					),
					revenue: that.getSummaryStatRevenue(contractsByYear[year] ?? []),
				});
			});

			return contracts;
		},
	},
};
</script>
